// Cast Detail Panel
// Full panel view showing cast details with tabs
// Note: Header with back button, cast name, type is now in ContentFilterBar

import { useState, useEffect, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores/appStore";
import { LoraWizard } from "@/components/cast/lora-wizard";
import { OverviewTab } from "@/components/cast/OverviewTab";
import type { CastData, CastType } from "./CastContainer";
import { CAST_TYPE_COLORS, CAST_TYPE_COLORS_RGB } from "./CastContainer";
import type { Lora, LoraKindSlug } from "@/types/loras";
import { useAssetUrl } from "@/hooks/loras/useAssetUrl";
import { User, Star, Plus, Sparkles, type LucideProps } from "lucide-react";
import "@/components/dashboard/WorkflowGrid.css"; // For project-tab styles

// Cast is characters only - single icon
const CAST_TYPE_ICONS: Record<CastType, React.FC<LucideProps>> = {
  character: User,
};

// LoRA data interface
interface LoRAData {
  id: string;
  uniqueId: string;
  name: string;
  category: "CHARACTER" | "LOCATION" | "PROP" | "STYLE";
  description: string;
  imageUrl: string | null;
  refs: number;
  gaps: number;
  createdAt: string;
  progress: number;
  isFeatured?: boolean;
}

// Category colors for LoRAs - using CSS custom properties
const CATEGORY_COLORS: Record<LoRAData["category"], string> = {
  CHARACTER: "var(--color-primitive-pink-500)",
  LOCATION: "var(--color-primitive-green-500)",
  PROP: "var(--color-primitive-amber-500)",
  STYLE: "var(--color-primitive-blue-500)",
};

// RGB channel variants for rgba() opacity expressions
const CATEGORY_COLORS_RGB: Record<LoRAData["category"], string> = {
  CHARACTER: "var(--color-primitive-pink-500-rgb)",
  LOCATION: "var(--color-primitive-green-500-rgb)",
  PROP: "var(--color-primitive-amber-500-rgb)",
  STYLE: "var(--color-primitive-blue-500-rgb)",
};

/**
 * Map LoRA kindSlug to display category
 */
function loraKindToCategory(kind: LoraKindSlug): LoRAData["category"] {
  switch (kind) {
    case "subject":
      return "CHARACTER";
    case "style":
      return "STYLE";
    case "concept":
      return "LOCATION";
    default:
      return "CHARACTER";
  }
}

/**
 * Calculate progress percentage based on LoRA status
 */
function statusToProgress(status: string): number {
  switch (status) {
    case "ready":
      return 100;
    case "training":
      return 60;
    case "prepared":
      return 40;
    case "pending":
      return 10;
    case "failed":
    case "cancelled":
      return 0;
    default:
      return 0;
  }
}

/**
 * Format date to relative time (e.g., "2d ago")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMinutes > 0) return `${diffMinutes}m ago`;
  return "just now";
}

/**
 * Convert Lora API data to display format
 */
function loraToDisplayData(lora: Lora): LoRAData {
  return {
    id: lora.id,
    uniqueId: `PSP-${lora.id.substring(0, 4).toUpperCase()}`,
    name: lora.name,
    category: loraKindToCategory(lora.kindSlug),
    description:
      lora.description || `${lora.kindSlug} LoRA - ${lora.baseModelSlug}`,
    imageUrl: null, // Resolved at render time from lora.thumbnailAssetId.
    refs: 0, // TODO: Get from dataset prep
    gaps: 0, // TODO: Get from script analysis
    createdAt: formatRelativeTime(lora.createdAt),
    progress: statusToProgress(lora.statusSlug),
    isFeatured: lora.statusSlug === "ready",
  };
}

type TabId = "overview" | "loras" | "train" | "scripts" | "context" | "history";

interface Tab {
  id: TabId;
  label: string;
  /** Tab is non-functional and should be greyed out */
  disabled?: boolean;
  /** Show "Coming soon" tooltip for disabled tabs */
  comingSoon?: boolean;
}

const TABS: Tab[] = [
  { id: "overview", label: "Overview" },
  { id: "loras", label: "LoRAs" },
  { id: "train", label: "Train LoRA" },
  { id: "scripts", label: "Script & Gaps", disabled: true, comingSoon: true },
  { id: "context", label: "Smart Context", disabled: true, comingSoon: true },
  { id: "history", label: "History", disabled: true, comingSoon: true },
];

interface EditableAttribute {
  id: string;
  key: string;
  value: string;
}

const MAX_ATTRIBUTES = 10;

interface CastDetailPanelProps {
  cast: CastData;
  lora?: Lora;
  activeTab: string;
  onBack: () => void;
  onTabChange: (tab: string) => void;
}

export function CastDetailPanel({
  cast,
  lora,
  activeTab: activeTabProp,
  onBack: _onBack, // Keeping in interface for compatibility
  onTabChange,
}: CastDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>(
    (activeTabProp as TabId) || "overview",
  );
  const { currentProject } = useAppStore();

  // Convert lora to display format
  const loraDisplayData = useMemo(() => {
    if (!lora) return null;
    return loraToDisplayData(lora);
  }, [lora]);

  // Resolve the LoRA's preview thumbnail (cover / checkpoint sample) to a
  // signed URL. Falls back to the kind icon when there is no thumbnail.
  const { data: resolvedThumbnailUrl } = useAssetUrl(lora?.thumbnailAssetId);
  const detailImageUrl =
    loraDisplayData?.imageUrl ?? resolvedThumbnailUrl ?? null;

  // Sync with prop
  useEffect(() => {
    if (activeTabProp && TABS.some((t) => t.id === activeTabProp)) {
      setActiveTab(activeTabProp as TabId);
    }
  }, [activeTabProp]);

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    onTabChange(tabId);
  };

  // Handle wizard completion - switch to LoRAs tab
  const handleWizardComplete = useCallback(() => {
    setActiveTab("loras");
    onTabChange("loras");
  }, [onTabChange]);

  // Editable attributes state
  const [attributes, setAttributes] = useState<EditableAttribute[]>([]);

  // Initialize attributes from cast data
  useEffect(() => {
    if (cast) {
      const initialAttrs = Object.entries(cast.attributes).map(
        ([key, value], index) => ({
          id: `attr-${index}`,
          key,
          value,
        }),
      );
      setAttributes(initialAttrs);
    }
  }, [cast]);

  const updateAttribute = (
    id: string,
    field: "key" | "value",
    newValue: string,
  ) => {
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === id ? { ...attr, [field]: newValue } : attr,
      ),
    );
  };

  const addAttribute = () => {
    if (attributes.length >= MAX_ATTRIBUTES) return;
    setAttributes((prev) => [
      ...prev,
      { id: `attr-${Date.now()}`, key: "", value: "" },
    ]);
  };

  const TypeIcon = CAST_TYPE_ICONS[cast.type];
  const typeColor = CAST_TYPE_COLORS[cast.type];
  const typeColorRgb = CAST_TYPE_COLORS_RGB[cast.type];

  return (
    <div className="flex flex-col h-full">
      {/* Tab content - tabs are now in ContentFilterBar header */}
      <div
        className={cn(
          "flex-1 overflow-y-auto p-6",
          activeTab === "train" ? "flex flex-col" : "",
        )}
      >
        {activeTab === "overview" && (
          <OverviewTab
            cast={{ ...cast, imageUrl: detailImageUrl }}
            typeColor={typeColor}
            typeColorRgb={typeColorRgb}
            TypeIcon={TypeIcon}
            attributes={attributes}
            lora={lora}
            onTabChange={(tab) => handleTabClick(tab as TabId)}
            onUpdateAttribute={updateAttribute}
            onAddAttribute={addAttribute}
            maxAttributes={MAX_ATTRIBUTES}
          />
        )}
        {activeTab === "loras" && (
          <div className="grid grid-cols-3 gap-4" style={{ maxWidth: "720px" }}>
            {/* Existing LoRA cards first */}
            {loraDisplayData && (
              <div
                key={loraDisplayData.id}
                className="rounded-lg overflow-hidden border border-white/10 bg-[var(--color-primitive-neutral-900)] hover:border-white/20 transition-colors cursor-pointer"
              >
                {/* Image area with ID badge - smaller aspect ratio */}
                <div
                  className="relative aspect-square"
                  style={{
                    background: detailImageUrl
                      ? undefined
                      : `linear-gradient(135deg, rgba(${CATEGORY_COLORS_RGB[loraDisplayData.category]}, 0.19), rgba(${CATEGORY_COLORS_RGB[loraDisplayData.category]}, 0.06))`,
                  }}
                >
                  {detailImageUrl ? (
                    <img
                      src={detailImageUrl}
                      alt={loraDisplayData.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User
                        className="w-10 h-10 opacity-30"
                        color={CATEGORY_COLORS[loraDisplayData.category]}
                      />
                    </div>
                  )}

                  {/* ID Badge */}
                  <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm">
                    <span className="text-[10px] font-mono text-white/80">
                      {loraDisplayData.uniqueId}
                    </span>
                  </div>

                  {/* Featured badge */}
                  {loraDisplayData.isFeatured && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-amber-500/90 flex items-center gap-1">
                      <Star className="w-2.5 h-2.5 fill-current" />
                      <span className="text-[10px] font-medium">READY</span>
                    </div>
                  )}
                </div>

                {/* Footer content - more compact */}
                <div className="p-2.5 bg-[var(--color-primitive-neutral-900)] text-left">
                  {/* Category label */}
                  <span
                    className="text-[9px] font-semibold tracking-wider block"
                    style={{ color: CATEGORY_COLORS[loraDisplayData.category] }}
                  >
                    {loraDisplayData.category}
                  </span>

                  {/* Name */}
                  <h3 className="text-xs font-medium text-foreground mt-0.5 text-left truncate">
                    {loraDisplayData.name}
                  </h3>

                  {/* Stats row */}
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                    <span>{loraDisplayData.createdAt}</span>
                    <div className="flex-1" />
                    {/* Progress bar */}
                    <div className="flex items-center gap-1.5">
                      <div className="w-8 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${loraDisplayData.progress}%`,
                            backgroundColor:
                              CATEGORY_COLORS[loraDisplayData.category],
                          }}
                        />
                      </div>
                      <span className="font-medium">
                        {loraDisplayData.progress}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Train New LoRA Card - at the end */}
            <div
              onClick={() => handleTabClick("train")}
              className="rounded-lg overflow-hidden border-2 border-dashed border-white/20 hover:border-primary/50 bg-[var(--color-primitive-neutral-900)]/50 hover:bg-[var(--color-primitive-neutral-900)] transition-all cursor-pointer group"
            >
              <div className="aspect-square flex flex-col items-center justify-center gap-2">
                <div className="w-10 h-10 rounded-full border-2 border-dashed border-white/30 group-hover:border-primary/50 flex items-center justify-center transition-colors">
                  <Plus className="w-5 h-5 text-white/40 group-hover:text-primary transition-colors" />
                </div>
                <div className="flex items-center gap-1.5 text-white/50 group-hover:text-primary transition-colors">
                  <Sparkles className="w-3 h-3" />
                  <span className="text-xs font-medium">Train New</span>
                </div>
              </div>
              <div className="p-2.5 bg-[var(--color-primitive-neutral-900)] text-left">
                <p className="text-[10px] text-muted-foreground">
                  Create new LoRA
                </p>
              </div>
            </div>
          </div>
        )}
        {activeTab === "train" && (
          <LoraWizard
            castName={cast.name}
            loraId={cast.id}
            libraryProjectId={currentProject?.id}
            onComplete={handleWizardComplete}
          />
        )}
        {activeTab === "scripts" && (
          <div className="text-muted-foreground">
            <p>Script references and gap analysis for this cast member.</p>
            <p className="mt-2 text-sm">Coming soon...</p>
          </div>
        )}
        {activeTab === "context" && (
          <div className="text-muted-foreground">
            <p>Smart context and AI-generated insights.</p>
            <p className="mt-2 text-sm">Coming soon...</p>
          </div>
        )}
        {activeTab === "history" && (
          <div className="text-muted-foreground">
            <p>Version history and changes for this cast member.</p>
            <p className="mt-2 text-sm">Coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
}
